import React from "react";
import type { ApolloClient } from "@apollo/client";
import { Formik } from "formik";
import { Col, Form, Row } from "react-bootstrap";
import { isObject } from "lodash";
import MaskedInput from "components/MaskedInput";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { connect, type ConnectedProps } from "react-redux";
import moment from "moment";
import { push } from "store/actions/router";

import { kybRoles } from "statics/businessCodes";
import {
  address2Validator,
  DOB_FORMAT_ERROR,
  emailValidator,
  IndividualAddress1Validator,
  INVALID_PHONE_NUMBER,
  INVALID_SSN_ERROR,
  parseDob,
  postalCodeValidator,
  stateValidator,
  testDobFormat,
  testPhoneNumber,
  testSsn,
  validateUsersAddress,
  whiteSpaceFieldTrimmer,
} from "utils/fieldValidators";
import { userService } from "services/userService";

import StateSelect from "components/StateSelect";
import Alert from "components/Alert";
import Button from "components/Button";
import AddressValidator from "components/AddressValidator";
import { ScrollToFieldError } from "utils/form";
import PlanCard from "pages/signUp/employer/PlanCard";

import * as yup from "yup";

// A business member as consumed by the duplicate-SSN check below. PropTypes
// typed `members` as a bare array; only `id` and `memberProfile.ssn` are read.
interface Member {
  id?: string;
  memberProfile?: { ssn?: string } | null;
}

// The form's Formik values. initialValues spreads `ownerInfo` (a plain object)
// plus the derived confirmSsn/confirmDob, so every field is string-shaped to
// match initialValues except ownershipStake, which the render reads as
// `== null` (number | null) and the schema validates as a number.
interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  dob: string;
  confirmDob: string;
  ssn: string;
  confirmSsn: string;
  role: string;
  ownershipStake: number | null;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  phone: string;
}

// The owner record passed in via props. Only `id` is read directly; the rest
// flow through Formik initialValues. All optional — a `{}` ownerInfo is valid.
interface OwnerInfo extends Partial<FormValues> {
  id?: string;
}

// Mirrors AddressValidator's (module-local, unexported) AddressValidatorAddress:
// the address-shaped fields it reads plus the string index signature FormValues
// lacks. Used only as the boundary-cast target when forwarding Formik values.
interface AddressValidatorAddress {
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  businessName?: string;
  firstName?: string;
  lastName?: string;
  [key: string]: unknown;
}

// Custom SSN validation function to check for duplicates
const createSsnDuplicateValidator = (
  members: Member[],
  currentMemberId: string | undefined
) => {
  return function (this: yup.TestContext, value: string | undefined) {
    // Check if SSN matches any existing member (except current one being edited)
    const duplicateMember = members.find(
      (member) =>
        member.memberProfile &&
        member.memberProfile.ssn === value &&
        member.id !== currentMemberId
    );

    if (duplicateMember) {
      return this.createError({
        message: "This SSN is already associated with another business member",
        path: "ssn",
      });
    }

    return true;
  };
};

// Create schema with members data
const createSchema = (
  members: Member[],
  currentMemberId: string | undefined,
  isAdminPortal: boolean | undefined
) =>
  yup.object({
    firstName: yup
      .string()
      .label("First")
      .required()
      .min(1, "Must be at least one character")
      .max(30),
    lastName: yup
      .string()
      .label("Last")
      .required()
      .min(1, "Must be at least one character")
      .max(40),
    email: emailValidator("You must enter a valid email address")
      .label("Email")
      .max(200)
      .required(),
    dob: yup
      .string()
      .label("Date of Birth")
      .required()
      .test("dob-format", DOB_FORMAT_ERROR, testDobFormat)
      .test("dob", "You must be at least 18 to create an account", (value) => {
        return (
          parseDob(value).isValid() &&
          moment().diff(parseDob(value), "years") >= 18
        );
      }),
    confirmDob: yup
      .string()
      .trim()
      .required()
      .label("Confirm Date of Birth")
      .test("dob-match", "Date of births must match", function (value) {
        return this.parent.dob === value;
      }),
    ssn: yup
      .string()
      .trim()
      .required()
      .label("Social Security Number")
      // found-not-fixed: testSsn passed directly to .test() — yup invokes it as
      // (value, context), so its second param `allowNoDash` receives the truthy
      // TestContext, silently loosening SSN format validation (the documented
      // allowNoDash footgun). Preserved verbatim; the canonical fix is a lambda.
      // @ts-expect-error -- testSsn's allowNoDash:boolean param is incompatible with yup's TestContext; preserving the bug
      .test("ssn", INVALID_SSN_ERROR, testSsn)
      .test(
        "ssn-duplicate",
        "This SSN is already associated with another business member",
        function (value) {
          // Only run duplicate validation if in admin portal and we have members data
          if (!value || !isAdminPortal || !members || members.length === 0) {
            return true; // Skip duplicate validation for non-admin users
          }
          return createSsnDuplicateValidator(members, currentMemberId).call(
            this,
            value
          );
        }
      ),
    confirmSsn: yup
      .string()
      .trim()
      .required()
      .label("Confirm Social Security Number")
      .test(
        "ssn-match",
        "Social Security Numbers must match",
        function (value) {
          return this.parent.ssn === value;
        }
      ),
    role: yup.string().required().label("Please choose a Role").max(200),
    ownershipStake: yup
      .number()
      .integer("Must be a whole number")
      .when("role", {
        is: "ControllingOfficer",
        then: (s) => s.nullable(),
        otherwise: (s) =>
          s
            .required()
            .min(
              25,
              "Must be at least 25%, if less please select Controller / Officer role"
            )
            .max(100, "Cannot be more than 100%"),
      })
      .label("Ownership Percentage"),
    address1: IndividualAddress1Validator,
    address2: address2Validator,
    city: yup.string().label("City").required().max(40),
    state: stateValidator,
    postalCode: postalCodeValidator,
    phone: yup
      .string()
      .label("Phone")
      .required()
      .test("us-phone", INVALID_PHONE_NUMBER, testPhoneNumber)
      .max(30),
  });

const mapDispatchToProps = {
  push,
};

const connector = connect(null, mapDispatchToProps);

// Props the parent KYB flow passes directly to this child form. PropTypes had
// these all optional; tightened to real usage. `isFetching` was PropTypes.func
// but is read as a boolean (`disabled`/`loading`) — typed boolean to match.
interface OwnProps {
  ownerInfo: OwnerInfo;
  error?: string;
  isFetching?: boolean;
  isAdminPortal?: boolean;
  companyId?: string;
  planCode?: string;
  // submit path: a parent callback (NOT a dispatched mutation). The form
  // validates the address via props.client, then hands the trimmed identity to
  // this callback. Invoked unconditionally in submitUser, so required.
  onSubmit: (identity: unknown) => void;
  members: Member[];
}

type Props = WithApolloClient<ConnectedProps<typeof connector> & OwnProps>;

interface State {
  fetchingAddress: boolean;
  showAddressModal: boolean;
  validatedAddress: Record<string, unknown>;
}

class ControllerForm extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      fetchingAddress: false,
      showAddressModal: false,
      validatedAddress: {},
    };
  }

  submitUser = async (values: FormValues, skipAddressValidation = false) => {
    // verify user address, if it matches USPS just continue on
    // if USPS endpoint fails continue on
    // if there is a mismatch then open modal to allow customer to decide which to use
    if (!skipAddressValidation) {
      try {
        this.setState({ fetchingAddress: true });

        const userAddress = {
          address1: values.address1,
          address2: values.address2,
          state: values.state,
          postalCode: values.postalCode,
          city: values.city,
        };

        const validatedAddress = await userService.getValidatedAddress(
          this.props.client as ApolloClient<object>,
          userAddress
        );

        this.setState({ fetchingAddress: false });

        const userAddressIsValid = validateUsersAddress(
          userAddress,
          // GraphQL returns string|null fields (or null); validateUsersAddress
          // reads them as optional strings. Behavior is preserved — the runtime
          // value is unchanged, only the nullable-field shape differs.
          validatedAddress as unknown as Parameters<
            typeof validateUsersAddress
          >[1]
        );

        if (!userAddressIsValid) {
          this.setState({
            showAddressModal: true,
            // same nullable-vs-Record shape gap; store the raw value unchanged.
            validatedAddress: validatedAddress as unknown as Record<
              string,
              unknown
            >,
          });

          return;
        }
      } catch (error) {
        // if the validation endpoint fails we dont want to prevent user from saving so continue on and log error
        console.log(error);
      }
    }

    // parseFloat coerces its arg to string at runtime, so a number|null input
    // behaves identically; cast only to satisfy the string parameter type.
    let stake = parseFloat(values.ownershipStake as unknown as string);
    if (values.role === "ControllingOfficer") {
      stake = 0;
    }

    let identity = {
      employerId: this.props.companyId,
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      ssn: values.ssn,
      role: values.role,
      ownershipStake: stake,
      dob: parseDob(values.dob).format("YYYY/MM/DD"),
      address1: values.address1,
      address2: values.address2 || "", //graphql does not like null instead of empty string
      city: values.city,
      state: values.state,
      postalCode: values.postalCode,
      phone: values.phone,
      id: this.props.ownerInfo.id || "",
    };
    this.props.onSubmit(whiteSpaceFieldTrimmer(identity));
  };

  _closeModal = () => {
    this.setState({ showAddressModal: false });
  };

  render() {
    const btnLabel = this.props.isAdminPortal ? "Submit User" : "Continue";

    var articleStyle = {
      paddingBottom: 0,
    };

    const ownerInfo: OwnerInfo = isObject(this.props.ownerInfo)
      ? this.props.ownerInfo
      : {};

    return (
      <div className={"controller-form"}>
        <Formik
          validateOnChange={false}
          validationSchema={createSchema(
            this.props.members,
            this.props.ownerInfo?.id,
            this.props.isAdminPortal
          )}
          onSubmit={(values) => {
            void this.submitUser(values);
          }}
          enableReinitialize={true}
          // initialValues spreads the (partial) ownerInfo plus the two derived
          // confirm fields; cast to the full FormValues shape (fields absent at
          // runtime surface as undefined, exactly as before).
          initialValues={
            {
              ...ownerInfo,
              confirmSsn: ownerInfo.ssn || "",
              confirmDob: ownerInfo.dob || "",
            } as FormValues
          }
        >
          {({
            handleSubmit,
            handleChange,
            handleBlur,
            setFieldValue,
            values,
            touched,
            errors,
          }) => (
            <Form noValidate onSubmit={handleSubmit}>
              <ScrollToFieldError />
              {this.state.showAddressModal && (
                <AddressValidator
                  show={this.state.showAddressModal}
                  // submitUser takes the concrete FormValues; AddressValidator
                  // types onSubmit against the looser AddressValidatorAddress.
                  // The address fields it forwards are a subset, so the call is
                  // safe at runtime.
                  // @ts-expect-error -- FormValues is narrower than AddressValidatorAddress (param contravariance)
                  onSubmit={this.submitUser}
                  setFieldValue={setFieldValue}
                  validatedAddress={this.state.validatedAddress}
                  onClose={this._closeModal}
                  // FormValues has no string index signature; AddressValidator
                  // types `values` as the indexable AddressValidatorAddress. It
                  // only reads address-shaped fields, all present here, so the
                  // runtime value is a faithful superset.
                  values={values as unknown as AddressValidatorAddress}
                  allowPoBox={true}
                />
              )}
              <div className="mega-container">
                <div
                  className="step-container is-active"
                  data-circle-percent="100"
                >
                  <section className="page-title-wrap">
                    <article className="text-cell">
                      <p className="page-title">Contact Verification</p>
                    </article>
                  </section>

                  <div id="form-employer-profile">
                    <section className="form-sec-2col">
                      <article className="col-form" style={articleStyle}>
                        <article className="text-cell">
                          <p className="page-label">
                            {`${
                              values.role === "ControllingOfficer"
                                ? "Controller"
                                : "Owner"
                            } Personal Information`}{" "}
                          </p>
                        </article>
                        <Row className="mb-3">
                          <Form.Group as={Col} controlId="formBasicFirstName">
                            <Form.Control
                              name="firstName"
                              placeholder="Legal first name"
                              value={values.firstName}
                              onBlur={handleBlur}
                              onChange={handleChange}
                              isInvalid={
                                touched.firstName && !!errors.firstName
                              }
                              isValid={touched.firstName && !errors.firstName}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.firstName}
                            </Form.Control.Feedback>
                          </Form.Group>
                          <Form.Group as={Col} controlId="formBasicLastName">
                            <Form.Control
                              name="lastName"
                              placeholder="Legal last name"
                              value={values.lastName}
                              onChange={handleChange}
                              isInvalid={touched.lastName && !!errors.lastName}
                              isValid={touched.lastName && !errors.lastName}
                              onBlur={handleBlur}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.lastName}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Row>
                        <Row className="mb-3">
                          <Form.Group as={Col} controlId="formBasicEmail">
                            <Form.Control
                              type="email"
                              name="email"
                              placeholder="Work Email Address"
                              value={values.email}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isInvalid={touched.email && !!errors.email}
                              isValid={touched.email && !errors.email}
                              disabled={this.props.isAdminPortal ? false : true}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.email}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Row>
                        <Row className="mb-3">
                          <Form.Group as={Col} controlId="formBasicDateOfBirth">
                            <Form.Label>Date of Birth</Form.Label>
                            <Form.Control
                              as={MaskedInput}
                              mask="00/00/0000"
                              name="dob"
                              placeholder="MM/DD/YYYY"
                              value={values.dob}
                              onAccept={(val: unknown, mask: unknown) =>
                                handleChange({
                                  target: {
                                    name: (
                                      mask as {
                                        el: { input: HTMLInputElement };
                                      }
                                    ).el.input.name,
                                    value: val,
                                  },
                                } as unknown as React.ChangeEvent<HTMLInputElement>)
                              }
                              onBlur={handleBlur}
                              isInvalid={touched.dob && !!errors.dob}
                              isValid={touched.dob && !errors.dob}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.dob}
                            </Form.Control.Feedback>
                          </Form.Group>
                          <Form.Group as={Col} controlId="formBasicDateOfBirth">
                            <Form.Label>Confirm Date of Birth</Form.Label>
                            <Form.Control
                              as={MaskedInput}
                              mask="00/00/0000"
                              name="confirmDob"
                              placeholder="MM/DD/YYYY"
                              value={values.confirmDob}
                              onAccept={(val: unknown, mask: unknown) =>
                                handleChange({
                                  target: {
                                    name: (
                                      mask as {
                                        el: { input: HTMLInputElement };
                                      }
                                    ).el.input.name,
                                    value: val,
                                  },
                                } as unknown as React.ChangeEvent<HTMLInputElement>)
                              }
                              onBlur={handleBlur}
                              isInvalid={
                                touched.confirmDob && !!errors.confirmDob
                              }
                              isValid={touched.confirmDob && !errors.confirmDob}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.confirmDob}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Row>
                        <Row className="mb-3">
                          <Form.Group as={Col} controlId="formBasicSsnField">
                            <Form.Label>Social Security Number</Form.Label>
                            <Form.Control
                              as={MaskedInput}
                              mask="000-00-0000"
                              name="ssn"
                              placeholder="111-11-1111"
                              value={values.ssn}
                              onAccept={(val: unknown, mask: unknown) =>
                                handleChange({
                                  target: {
                                    name: (
                                      mask as {
                                        el: { input: HTMLInputElement };
                                      }
                                    ).el.input.name,
                                    value: val,
                                  },
                                } as unknown as React.ChangeEvent<HTMLInputElement>)
                              }
                              onBlur={handleBlur}
                              isInvalid={touched.ssn && !!errors.ssn}
                              isValid={touched.ssn && !errors.ssn}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.ssn}
                            </Form.Control.Feedback>
                          </Form.Group>
                          <Form.Group
                            as={Col}
                            controlId="formBasicSsnConfirmField"
                          >
                            <Form.Label>
                              Confirm Social Security Number
                            </Form.Label>
                            <Form.Control
                              as={MaskedInput}
                              mask="000-00-0000"
                              name="confirmSsn"
                              placeholder="000-00-0000"
                              value={values.confirmSsn}
                              onAccept={(val: unknown, mask: unknown) =>
                                handleChange({
                                  target: {
                                    name: (
                                      mask as {
                                        el: { input: HTMLInputElement };
                                      }
                                    ).el.input.name,
                                    value: val,
                                  },
                                } as unknown as React.ChangeEvent<HTMLInputElement>)
                              }
                              onBlur={handleBlur}
                              isInvalid={
                                touched.confirmSsn && !!errors.confirmSsn
                              }
                              isValid={touched.confirmSsn && !errors.confirmSsn}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.confirmSsn}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Row>
                        <Row className="mb-3">
                          <Form.Group as={Col} sm={9} controlId="formBasicRole">
                            <Form.Select
                              name="role"
                              disabled={!this.props.isAdminPortal}
                              value={values.role}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isInvalid={touched.role && !!errors.role}
                              isValid={touched.role && !errors.role}
                            >
                              <option value="" disabled>
                                Role
                              </option>
                              {kybRoles.map((item) => (
                                <option value={item.value} key={item.value}>
                                  {item.description}
                                </option>
                              ))}
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">
                              {errors.role}
                            </Form.Control.Feedback>
                          </Form.Group>
                          {values.role === "ControllingOfficer" ||
                          values.role === "" ? (
                            <div></div>
                          ) : (
                            <Form.Group
                              as={Col}
                              sm={3}
                              controlId="formBasicOwnership"
                            >
                              <Form.Control
                                type="number"
                                min={0}
                                max={100}
                                step={1}
                                name="ownershipStake"
                                placeholder="xxx%"
                                value={
                                  values.ownershipStake == null
                                    ? ""
                                    : values.ownershipStake
                                }
                                onChange={handleChange}
                                onBlur={handleBlur}
                                isInvalid={
                                  touched.ownershipStake &&
                                  !!errors.ownershipStake
                                }
                                isValid={
                                  touched.ownershipStake &&
                                  !errors.ownershipStake
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {errors.ownershipStake}
                              </Form.Control.Feedback>
                            </Form.Group>
                          )}
                        </Row>
                      </article>
                      <PlanCard planCode={this.props.planCode} />
                    </section>

                    <section className="form-sec-2col">
                      <article className="col-form">
                        <article className="text-cell-officer">
                          <p className="page-label">
                            {`${
                              values.role === "ControllingOfficer"
                                ? "Controller"
                                : "Owner"
                            } Personal Address`}{" "}
                          </p>
                        </article>
                        <Form.Group
                          className="mb-3"
                          controlId="formBasicaddress1"
                        >
                          <Form.Control
                            name="address1"
                            placeholder="Address 1"
                            value={values.address1}
                            onChange={handleChange}
                            isInvalid={touched.address1 && !!errors.address1}
                            isValid={touched.address1 && !errors.address1}
                            onBlur={handleBlur}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.address1}
                          </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group
                          className="mb-3"
                          controlId="formBasicaddress2"
                        >
                          <Form.Control
                            name="address2"
                            placeholder="Address 2"
                            value={values.address2}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            isInvalid={touched.address2 && !!errors.address2}
                            isValid={touched.address2 && !errors.address2}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.address2}
                          </Form.Control.Feedback>
                        </Form.Group>
                        <Row className="mb-3">
                          <Form.Group as={Col} controlId="formBasicCity">
                            <Form.Control
                              name="city"
                              placeholder="City"
                              value={values.city}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isInvalid={touched.city && !!errors.city}
                              isValid={touched.city && !errors.city}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.city}
                            </Form.Control.Feedback>
                          </Form.Group>

                          <Form.Group
                            as={Col}
                            sm={4}
                            controlId="formBasicState"
                          >
                            <StateSelect
                              value={values.state}
                              handleChange={handleChange}
                              handleBlur={handleBlur}
                              touched={touched}
                              errors={errors}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.state}
                            </Form.Control.Feedback>
                          </Form.Group>

                          <Form.Group
                            as={Col}
                            sm={3}
                            controlId="formBasicPostalCode"
                          >
                            <Form.Control
                              name="postalCode"
                              placeholder="Zip"
                              value={values.postalCode}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isInvalid={
                                touched.postalCode && !!errors.postalCode
                              }
                              isValid={touched.postalCode && !errors.postalCode}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.postalCode}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Row>
                        <Row className="mb-3">
                          <Form.Group
                            as={Col}
                            sm={5}
                            controlId="formBasicPhone"
                          >
                            <Form.Control
                              as={MaskedInput}
                              mask="000-000-0000"
                              name="phone"
                              placeholder="Personal Phone"
                              value={values.phone}
                              onAccept={(val: unknown, mask: unknown) =>
                                handleChange({
                                  target: {
                                    name: (
                                      mask as {
                                        el: { input: HTMLInputElement };
                                      }
                                    ).el.input.name,
                                    value: val,
                                  },
                                } as unknown as React.ChangeEvent<HTMLInputElement>)
                              }
                              onBlur={handleBlur}
                              isInvalid={touched.phone && !!errors.phone}
                              isValid={touched.phone && !errors.phone}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.phone}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Row>
                      </article>
                    </section>
                    {this.props.error && (
                      <Alert type="error" msg={this.props.error} />
                    )}
                    {this.props.isAdminPortal && (
                      <Button
                        btnLabel={"Back To Summary"}
                        name="action"
                        type={"button"}
                        color="cancel"
                        onClick={() => {
                          this.props.push(
                            `/employers/${String(this.props.companyId)}/kyb/summary`
                          );
                        }}
                      />
                    )}
                    <Button
                      name="submit"
                      withArrow={true}
                      btnLabel={btnLabel}
                      disabled={
                        this.props.isFetching || this.state.fetchingAddress
                      }
                      loading={
                        this.props.isFetching || this.state.fetchingAddress
                      }
                    />
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    );
  }
}

export default connector(withApollo<Props>(ControllerForm));
